import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import {
  useReadNotificationMutation,
  useSubscribeToNewsMutation,
  useUnsubscribeToNewsMutation,
  useUserMeQuery,
} from '@/graphql';
import {
  Box,
  Dialog,
  DialogActions,
  Button,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from '@material-ui/core';
import SmallLogoIcon from '@/components/icons/small-logo';
import { USER_FRAGMENT } from '@/apollo/fragments';
import { useModalSlot } from '@/contexts/modal-coordinator-context';
import { ModalSlotId } from '@/type';

interface SubscribeNewslettersProps {
  notificationId?: string;
  mode?: 'notification' | 'onboarding';
  onClose?: () => void;
}

const SubscribeNewslettersModal: React.FC<SubscribeNewslettersProps> = ({
  notificationId,
  mode = 'notification',
  onClose: onCloseProp,
}) => {
  const client = useApolloClient();
  const [readNotification] = useReadNotificationMutation({ context: { errorHandling: 'notify' } });
  const { t } = useTranslation();
  const [opened, setOpened] = useState(true);

  // Only the notification usage competes in the priority queue; the onboarding variant is driven
  // by its own flow and shows independently.
  const isNotificationMode = mode === 'notification';
  const { isActive } = useModalSlot(ModalSlotId.SubscribeNewsletters, isNotificationMode && opened);

  const { data } = useUserMeQuery({
    fetchPolicy: 'cache-first',
  });

  const [subscribe] = useSubscribeToNewsMutation({ context: { errorHandling: 'notify' } });
  const [unsubscribe] = useUnsubscribeToNewsMutation({ context: { errorHandling: 'notify' } });
  const userId = data?.me?.id;

  const onClose = useCallback(() => {
    if (notificationId) {
      readNotification({
        variables: { id: notificationId },
      }).catch(() => {});
    }
    setOpened(false);
    onCloseProp?.();
  }, [notificationId, onCloseProp, readNotification]);

  const updateLocalNewsletterState = useCallback(
    (nextValue: boolean) => {
      client.writeFragment({
        id: `${data?.me?.__typename}:${data?.me?.id}`,
        fragment: USER_FRAGMENT,
        data: {
          ...data?.me,
          subscribeToNews: nextValue,
        },
      });
    },
    [client, data]
  );

  const subscribeToNewsletters = useCallback(async () => {
    try {
      if (!data?.me?.subscribeToNews) {
        await subscribe({
          variables: {
            userId,
          },
        });
        updateLocalNewsletterState(true);
      }
    } catch {
      /* swallowed; Apollo link reports it */
    } finally {
      onClose();
    }
  }, [data?.me?.subscribeToNews, onClose, subscribe, updateLocalNewsletterState, userId]);

  const continueWithoutEmail = useCallback(async () => {
    try {
      if (data?.me?.subscribeToNews) {
        await unsubscribe({
          variables: {
            userId,
          },
        });
        updateLocalNewsletterState(false);
      }
    } catch {
      /* swallowed; Apollo link reports it */
    } finally {
      onClose();
    }
  }, [data?.me?.subscribeToNews, onClose, unsubscribe, updateLocalNewsletterState, userId]);

  const title =
    mode === 'onboarding'
      ? 'Fachlich auf dem Laufenden bleiben?'
      : t('dialogs.subscribe-newsletters.title');
  const text =
    mode === 'onboarding'
      ? 'Wir informieren Sie über neue Leitlinien-Trainings und CME-Module.'
      : t('dialogs.subscribe-newsletters.text');
  const primaryLabel =
    mode === 'onboarding'
      ? 'Ja, per E-Mail informieren'
      : t('dialogs.subscribe-newsletters.button');
  const secondaryLabel =
    mode === 'onboarding'
      ? 'Ohne E-Mail fortfahren'
      : t('dialogs.subscribe-newsletters.button2');

  return (
    <Dialog
      data-cy-subscribe-newsletters-dialog
      open={isNotificationMode ? isActive && opened : opened}
      onClose={onClose}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box marginBottom={3}>
          <SmallLogoIcon />
        </Box>
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">
          <Box component="span" marginBottom={2}>
            {text}
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box
          width="100%"
          display="grid"
          gridRowGap={12}
          flexDirection="column"
          padding={4}
          paddingTop={0}
        >
          <Button data-cy-subscribe-btn onClick={subscribeToNewsletters} color="primary" variant="contained">
            {primaryLabel}
          </Button>

          <Button data-cy-later-btn variant="outlined" onClick={continueWithoutEmail}>
            {secondaryLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubscribeNewslettersModal;
